import { useCallback, useEffect, useState } from 'react';

import { api } from '@/services/api';
import { getUserFriendlyMessage } from '@/utils/errors';

import type {
  Channel,
  Currency,
  Location,
  LocationFormData,
  PickupMethod,
  TaxZone,
} from '../components/locations/types';

export interface UseLocationsResult {
  // Data
  locations: Location[];
  currencies: Currency[];
  taxZones: TaxZone[];
  channels: Channel[];
  pickupMethods: PickupMethod[];
  storeChannelId: number;

  // State
  isLoading: boolean;
  error: string | null;
  isSaving: boolean;

  // Actions
  loadData: () => Promise<void>;
  enableLocation: (locationId: number, data: Omit<LocationFormData, 'taxZoneId' | 'pickupMethodId'> & { taxZoneId?: number; pickupMethodId?: number }) => Promise<void>;
  updateLocation: (locationId: number, data: Omit<LocationFormData, 'taxZoneId' | 'pickupMethodId'> & { taxZoneId?: number; pickupMethodId?: number }) => Promise<void>;
  disableLocation: (locationId: number) => Promise<boolean>;
  clearError: () => void;

  // Helpers
  getTaxZoneName: (taxZoneId?: number) => string;
  getDefaultCurrency: () => string;
  getDefaultPickupMethod: (locationId: number) => PickupMethod | undefined;
}

export function useLocations(): UseLocationsResult {
  const [locations, setLocations] = useState<Location[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [taxZones, setTaxZones] = useState<TaxZone[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [pickupMethods, setPickupMethods] = useState<PickupMethod[]>([]);
  const [storeChannelId, setStoreChannelId] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadCurrencies = useCallback(async () => {
    try {
      const response = await api.getCurrencies();
      setCurrencies(response.currencies);
    } catch (err) {
      console.error('Failed to load currencies:', err);
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to load currencies: ${message}`);
    }
  }, []);

  const loadTaxZones = useCallback(async () => {
    try {
      const response = await api.getTaxZones();
      setTaxZones(response.zones.filter((z: TaxZone) => z.enabled));
    } catch (err) {
      console.error('Failed to load tax zones:', err);
      const message = err instanceof Error ? err.message : 'Unknown error';
      if (!error) {
        setError(`Failed to load tax zones: ${message}`);
      }
    }
  }, [error]);

  const loadChannels = useCallback(async () => {
    try {
      const response = await api.getChannels();
      // Filter out channel 1 (default storefront) and disabled channels
      const enabledChannels = response.channels.filter(
        (c: Channel) => c.id !== 1 && c.isEnabled
      );
      setChannels(enabledChannels);
      setStoreChannelId(response.currentChannelId);
    } catch (err) {
      console.error('Failed to load channels:', err);
      // Non-critical - don't show error for channels
    }
  }, []);

  const loadPickupMethods = useCallback(async () => {
    try {
      const response = await api.getPickupMethods();
      setPickupMethods(response.methods || []);
    } catch (err) {
      console.error('Failed to load pickup methods:', err);
      // Non-critical - don't show error, pickup methods may not be available
    }
  }, []);

  const loadLocations = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.getLocations();
      setLocations(response.locations);
    } catch (err) {
      setError(getUserFriendlyMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadData = useCallback(async () => {
    await Promise.all([loadLocations(), loadCurrencies(), loadTaxZones(), loadChannels(), loadPickupMethods()]);
  }, [loadLocations, loadCurrencies, loadTaxZones, loadChannels, loadPickupMethods]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const enableLocation = useCallback(async (
    locationId: number,
    data: Omit<LocationFormData, 'taxZoneId' | 'pickupMethodId'> & { taxZoneId?: number; pickupMethodId?: number }
  ) => {
    try {
      setIsSaving(true);
      await api.enableLocation(locationId, data);

      setLocations(prev =>
        prev.map(loc =>
          loc.id === locationId
            ? {
                ...loc,
                isConfiguredForPOS: true,
                posSettings: {
                  locationId: loc.id,
                  displayName: data.displayName,
                  currency: data.currency,
                  taxZoneId: data.taxZoneId,
                  bcChannelId: data.bcChannelId ?? undefined,
                  pickupMethodId: data.pickupMethodId,
                  devices: loc.posSettings?.devices || [],
                  defaultReceiptMethod: data.defaultReceiptMethod,
                  requireCustomerEmail: data.requireCustomerEmail,
                  requireCustomerPhone: data.requireCustomerPhone,
                  paymentMethods: data.paymentMethods,
                  tip: data.tip,
                },
              }
            : loc
        )
      );
    } catch (err) {
      setError(getUserFriendlyMessage(err));
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, []);

  const updateLocation = useCallback(async (
    locationId: number,
    data: Omit<LocationFormData, 'taxZoneId' | 'pickupMethodId'> & { taxZoneId?: number; pickupMethodId?: number }
  ) => {
    try {
      setIsSaving(true);
      await api.updateLocation(locationId, data);

      setLocations(prev =>
        prev.map(loc =>
          loc.id === locationId
            ? {
                ...loc,
                posSettings: {
                  locationId: loc.id,
                  displayName: data.displayName,
                  currency: data.currency,
                  taxZoneId: data.taxZoneId,
                  bcChannelId: data.bcChannelId ?? undefined,
                  pickupMethodId: data.pickupMethodId,
                  devices: loc.posSettings?.devices || [],
                  defaultReceiptMethod: data.defaultReceiptMethod,
                  requireCustomerEmail: data.requireCustomerEmail,
                  requireCustomerPhone: data.requireCustomerPhone,
                  paymentMethods: data.paymentMethods,
                  tip: data.tip,
                },
              }
            : loc
        )
      );
    } catch (err) {
      setError(getUserFriendlyMessage(err));
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, []);

  const disableLocation = useCallback(async (locationId: number): Promise<boolean> => {
    if (!confirm('Are you sure you want to disable POS for this location? Devices will be unassigned.')) {
      return false;
    }

    try {
      await api.disableLocation(locationId);
      setLocations(prev =>
        prev.map(loc =>
          loc.id === locationId
            ? { ...loc, isConfiguredForPOS: false, posSettings: null }
            : loc
        )
      );
      return true;
    } catch (err) {
      setError(getUserFriendlyMessage(err));
      return false;
    }
  }, []);

  const getTaxZoneName = useCallback((taxZoneId?: number): string => {
    if (!taxZoneId) return 'Not set';
    const zone = taxZones.find(z => z.id === taxZoneId);
    return zone?.name || 'Unknown';
  }, [taxZones]);

  const getDefaultCurrency = useCallback((): string => {
    const defaultCurrency = currencies.find(c => c.isDefault);
    return defaultCurrency?.code || currencies[0]?.code || '';
  }, [currencies]);

  const getDefaultPickupMethod = useCallback((locationId: number): PickupMethod | undefined => {
    // First, try to find one already assigned to this location
    const forLocation = pickupMethods.find(m => m.locationId === locationId);
    if (forLocation) return forLocation;
    // Otherwise return the first available pickup method
    return pickupMethods[0];
  }, [pickupMethods]);

  const clearError = useCallback(() => setError(null), []);

  return {
    locations,
    currencies,
    taxZones,
    channels,
    pickupMethods,
    storeChannelId,
    isLoading,
    error,
    isSaving,
    loadData,
    enableLocation,
    updateLocation,
    disableLocation,
    clearError,
    getTaxZoneName,
    getDefaultCurrency,
    getDefaultPickupMethod,
  };
}
